import ActionTextIcon from "@/components/icons/ActionTextIcon";
import PopconfirmDeleteIcon from "@/components/icons/PopconfirmDeleteIcon";
import Ellipsis from "@/components/views/Ellipsis";
import { paginationTableProps, tableStandardProps } from "@/utils/componentUtils";
import { formatLocaleCurrency, formatLocaleDateTime } from "@/utils/formatUtils";
import { URLQuery } from "@/utils/queryUtils";
import { numberOrZero } from "@/utils/utils";
import { Card, Divider, Flex, Popconfirm, Table } from "antd";
import { ColumnsType } from "antd/es/table";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { generatePath, useNavigate } from "react-router";
import { useDeleteCommissionsBatch, useFilterCommissionsBatches } from "../../../../../common/api/queries";
import { TableSizes } from "../../../../../common/constants";
import { Permission } from "../../../../../common/security/enums";
import { useHasPermissions } from "../../../../auth/hooks";
import { CommissionsBatchStep } from "../../enums";
import { COMMISSIONS_BATCH_ROUTE_PATHS } from "../../paths";
import { CommissionsBatchFilterPageRequest, CommissionsBatchList } from "../../types";
import { CommissionsBatchStepTag } from "../CommissionsBatchStepTag";
import { UpdateCommissionsBatchForm } from "../forms/UpdateCommissionsBatchForm";

interface Props {
  searchParams: CommissionsBatchFilterPageRequest;
}

export const CommissionsBatchTable = ({ searchParams }: Props) => {
  const { t } = useTranslation();

  const navigate = useNavigate();

  const hasManageCommissionsPermission = useHasPermissions(Permission.COMMISSIONS_MANAGE);

  const [batchToUpdate, setBatchToUpdate] = useState<CommissionsBatchList>();

  const { data, isFetching } = useFilterCommissionsBatches(searchParams);

  const deleteCommissionsBatch = useDeleteCommissionsBatch();

  const columns = useMemo<ColumnsType<CommissionsBatchList>>(
    () => [
      {
        key: "name",
        title: t("commissions.batch.attrs.name"),
        width: 290,
        ellipsis: { showTitle: false },
        render: (_, record) => <Ellipsis>{record.name}</Ellipsis>,
      },
      {
        key: "step",
        title: t("commissions.batch.enums.batchStep._label"),
        width: 115,
        render: (_, record) => <CommissionsBatchStepTag step={record.step} style={{ marginRight: 0 }} />,
      },
      {
        key: "finishedAt",
        title: t("commissions.batch.attrs.finishedAt"),
        width: 175,
        render: (_, record) => (record.finishedAt ? formatLocaleDateTime(record.finishedAt) : undefined),
      },
      {
        key: "allCommissionAmount",
        title: t("commissions.batch.helpers.allCommissionAmount"),
        align: "right",
        width: 140,
        render: (_, record) => formatLocaleCurrency(record.allCommissionAmount ?? 0),
      },
      {
        key: "volumeCommissionAmount",
        title: t("commissions.batch.helpers.volumeCommissionAmountDesc"),
        align: "right",
        width: 150,
        render: (_, record) => formatLocaleCurrency(record.volumeCommissionAmount ?? 0),
      },
      {
        key: "actions",
        align: "right",
        fixed: "right",
        width: 270,
        render: (_, record) => (
          <Flex>
            <ActionTextIcon
              path={generatePath(COMMISSIONS_BATCH_ROUTE_PATHS.detail.to, { id: record.id })}
              icon="eye"
              color="green"
              text={t("common.show")}
            />

            {hasManageCommissionsPermission && (
              <>
                <Divider orientation="vertical" />

                <ActionTextIcon
                  icon="edit"
                  color="blue"
                  text={t("common.edit")}
                  disabled={record.step === CommissionsBatchStep.FINISH}
                  onClick={() => setBatchToUpdate(record)}
                />

                <Divider orientation="vertical" />

                <Popconfirm
                  title={t("commissions.batch.helpers.deleteBatchConfirm")}
                  icon={<PopconfirmDeleteIcon />}
                  okText={t("common.yes")}
                  cancelText={t("common.no")}
                  okType="danger"
                  disabled={record.step === CommissionsBatchStep.FINISH}
                  onConfirm={() => deleteCommissionsBatch.mutate(record.id)}
                >
                  <ActionTextIcon
                    icon="delete"
                    color="red"
                    text={t("common.delete")}
                    disabled={record.step === CommissionsBatchStep.FINISH}
                  />
                </Popconfirm>
              </>
            )}
          </Flex>
        ),
      },
    ],
    [hasManageCommissionsPermission],
  );

  return (
    <Card className="card-box">
      <Table<CommissionsBatchList>
        {...tableStandardProps()}
        columns={columns}
        scroll={{ x: TableSizes.LARGE }}
        dataSource={data?.pageData}
        loading={isFetching}
        pagination={{
          ...paginationTableProps,
          current: numberOrZero(data?.pageIndex) + 1,
          pageSize: data?.pageSize,
          total: data?.totalElementsCount,
          onChange: (pageIndex) => navigate({ search: URLQuery.getTableChangePageIndex(pageIndex) }, { replace: true }),
        }}
      />

      <UpdateCommissionsBatchForm
        batch={batchToUpdate}
        open={!!batchToUpdate}
        onFormCancel={() => setBatchToUpdate(undefined)}
      />
    </Card>
  );
};
